import { useState } from "react";
import { findSuperMerchCoupon, superRewardFromUrl } from "@/lib/superWheelCoupon";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Truck, Zap, Tag, Crown, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ShippingAddressForm, type ShippingAddress } from "@/components/recipe-book/ShippingAddressForm";
import { TermsOfSaleCheckbox } from "@/components/checkout/TermsOfSaleCheckbox";
import { DELIVERY_PRICES, CREATOR_DELIVERY_PRICES, type DeliveryPriceInfo } from "@/config/pricing";
import { displayPrice } from "@/config/merchProducts";
import { getPromotionByCode } from "@/config/promotions";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useMerchCart } from "@/contexts/MerchCartContext";
import type { SupportedCurrency } from "@/lib/currencyDetection";

function getDiscountedDelivery(
  base: DeliveryPriceInfo,
  deliveryType: "standard" | "express",
  isChefMaster: boolean,
  isFamilyMember: boolean,
): { amount: number; display: string; discountLabel: string | null; originalDisplay: string | null } {
  if (isChefMaster && !isFamilyMember) {
    if (deliveryType === "standard") {
      return { amount: 0, display: "FREE", discountLabel: "Chef Master", originalDisplay: base.display };
    }
    const discounted = Math.round(base.amount * 0.7);
    return { amount: discounted, display: formatCentsDisplay(discounted, base.display), discountLabel: "30% off", originalDisplay: base.display };
  }
  if (isFamilyMember) {
    const discounted = Math.round(base.amount * 0.9);
    return { amount: discounted, display: formatCentsDisplay(discounted, base.display), discountLabel: "Family 10% off", originalDisplay: base.display };
  }
  return { amount: base.amount, display: base.display, discountLabel: null, originalDisplay: null };
}

function formatCentsDisplay(cents: number, refDisplay: string): string {
  if (cents === 0) return "FREE";
  if (refDisplay.startsWith("€")) return `€${(cents / 100).toFixed(2)}`;
  if (refDisplay.startsWith("$")) return `$${(cents / 100).toFixed(2)}`;
  return `${Math.round(cents / 100)} kr`;
}

interface MerchCartCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: SupportedCurrency;
}

export function MerchCartCheckoutDialog({ open, onOpenChange, currency }: MerchCartCheckoutDialogProps) {
  const { t } = useTranslation();
  const { effectiveTier, isFamilyMember } = useSubscription();
  const { items, subtotalDKK, clear, setOpen } = useMerchCart();
  const [step, setStep] = useState<"address" | "review">("address");
  const [address, setAddress] = useState<ShippingAddress | null>(null);
  const [deliveryType, setDeliveryType] = useState<"standard" | "express">("standard");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState(superRewardFromUrl);
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number; freeSticker?: boolean } | null>(null);
  const [promoError, setPromoError] = useState("");
  const isChefMaster = effectiveTier === "chef_master";

  const hasCreatorItems = items.some((i) => i.storeOwnerId && !i.isOfficialStore);
  const deliveryPriceTable = hasCreatorItems ? CREATOR_DELIVERY_PRICES : DELIVERY_PRICES;

  const standardDiscount = getDiscountedDelivery(deliveryPriceTable.standard[currency], "standard", isChefMaster, isFamilyMember);
  const expressDiscount = getDiscountedDelivery(deliveryPriceTable.express[currency], "express", isChefMaster, isFamilyMember);
  const activeDelivery = deliveryType === "express" ? expressDiscount : standardDiscount;

  const deliveryPriceDKK = (() => {
    const dkkBase = deliveryPriceTable[deliveryType].DKK;
    const disc = getDiscountedDelivery(dkkBase, deliveryType, isChefMaster, isFamilyMember);
    return disc.amount / 100;
  })();

  // Promo applies only to Uhyggeligt items (server enforces this too)
  // Winner code = one free Uhyggeligt sticker (server enforces this too)
  const freeStickerPrice = items.find((i) => i.productId === "uhyggeligt-sticker")?.priceDKK ?? 0;
  const discountAmount = appliedPromo?.freeSticker
    ? freeStickerPrice
    : appliedPromo ? Math.round(subtotalDKK * appliedPromo.discount / 100) : 0;
  const baseDKK = subtotalDKK - discountAmount + deliveryPriceDKK;
  // Mirrors server: 2.5% + 2.50 kr processing fee (waived at 0 kr)
  const processingFeeDKK = baseDKK > 0 ? Math.round((baseDKK * 0.025 + 2.5) * 100) / 100 : 0;
  const totalDKK = baseDKK + processingFeeDKK;

  const handleApplyPromo = async () => {
    setPromoError("");
    if (promoCode.trim().toUpperCase() === "UHYGGELIG25") {
      if (!items.some((i) => i.productId === "uhyggeligt-sticker")) {
        setPromoError(t("merch.checkoutDialog.stickerOnly"));
        return;
      }
      setAppliedPromo({ code: "UHYGGELIG25", discount: 0, freeSticker: true });
      return;
    }
    const promo = getPromotionByCode(promoCode.trim());
    if (!promo) {
      const reward = await findSuperMerchCoupon(promoCode);
      if (reward) { setAppliedPromo({ code: reward.code, discount: reward.discount }); return; }
      setPromoError(t("merch.checkoutDialog.invalidPromo"));
      return;
    }
    const now = new Date();
    if (now < promo.startDate || now > promo.endDate) {
      setPromoError(t("merch.checkoutDialog.expiredPromo"));
      return;
    }
    setAppliedPromo({ code: promo.code, discount: promo.discountPercent });
  };

  const handleCheckout = async () => {
    if (!address || !termsAccepted || items.length === 0) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-merch-checkout", {
        body: {
          items: items.map((i) => ({
            productId: i.productId,
            size: i.size,
            color: i.color,
            gender: i.gender,
            quantity: i.quantity,
            storeOwnerId: i.storeOwnerId,
            isOfficialStore: i.isOfficialStore,
            productImageUrl: i.image,
          })),
          currency,
          deliveryType,
          promoCode: appliedPromo?.code || undefined,
          shippingAddress: {
            name: address.name,
            address: address.address1,
            address2: address.address2,
            city: address.city,
            zip: address.postal_code,
            country_code: address.country_code,
            email: address.email,
            phone: address.mobile,
          },
        },
      });

      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");

      clear();
      setOpen(false);
      window.open(data.url, "_blank");
      onOpenChange(false);
      toast.success(t("merch.checkoutRedirect"));
    } catch (err) {
      console.error("Cart checkout error:", err);
      toast.error(t("merch.checkoutError"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setStep("address");
      setTermsAccepted(false);
      setPromoCode("");
      setAppliedPromo(null);
      setPromoError("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("merch.cart.checkoutTitle")}</DialogTitle>
        </DialogHeader>

        {/* Items summary */}
        <div className="max-h-40 space-y-2 overflow-y-auto">
          {items.map((item) => (
            <div key={item.key} className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/50 p-2">
              <img src={item.image} alt={item.name} className="h-12 w-12 rounded-md object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{item.shortName}</p>
                <p className="text-xs text-muted-foreground">
                  {[item.size, item.color].filter(Boolean).join(" / ") || t("merch.cart.oneSize")} × {item.quantity}
                </p>
              </div>
              <p className="text-sm font-bold text-primary">{displayPrice(item.priceDKK * item.quantity, currency)}</p>
            </div>
          ))}
        </div>

        {step === "address" ? (
          <ShippingAddressForm
            onSubmit={(addr) => {
              setAddress(addr);
              setStep("review");
            }}
            onBack={() => handleClose(false)}
            initialAddress={address ?? undefined}
          />
        ) : (
          <div className="space-y-4">
            {/* Delivery */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t("merch.deliveryMethod")}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setDeliveryType("standard")}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-sm transition-colors ${
                    deliveryType === "standard"
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border text-muted-foreground hover:border-muted-foreground"
                  }`}
                >
                  <Truck className="h-4 w-4" />
                  <span className="font-medium">{t("merch.checkoutDialog.standard")}</span>
                  {standardDiscount.discountLabel ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-xs text-muted-foreground line-through">{standardDiscount.originalDisplay}</span>
                      <span className="text-xs font-semibold text-primary">{standardDiscount.display}</span>
                      <Badge variant="outline" className="gap-0.5 border-primary/30 px-1.5 py-0 text-[9px] text-primary">
                        {isChefMaster && !isFamilyMember ? <Crown className="h-2.5 w-2.5" /> : <Users className="h-2.5 w-2.5" />}
                        {standardDiscount.discountLabel}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-xs">{deliveryPriceTable.standard[currency].display}</span>
                  )}
                </button>
                <button
                  onClick={() => setDeliveryType("express")}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-sm transition-colors ${
                    deliveryType === "express"
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border text-muted-foreground hover:border-muted-foreground"
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  <span className="font-medium">{t("merch.checkoutDialog.express")}</span>
                  {expressDiscount.discountLabel ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-xs text-muted-foreground line-through">{expressDiscount.originalDisplay}</span>
                      <span className="text-xs font-semibold text-primary">{expressDiscount.display}</span>
                      <Badge variant="outline" className="gap-0.5 border-primary/30 px-1.5 py-0 text-[9px] text-primary">
                        {isChefMaster && !isFamilyMember ? <Crown className="h-2.5 w-2.5" /> : <Users className="h-2.5 w-2.5" />}
                        {expressDiscount.discountLabel}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-xs">{deliveryPriceTable.express[currency].display}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Address summary */}
            {address && (
              <div className="rounded-lg border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
                <p className="mb-1 text-sm font-medium text-foreground">{t("merch.shippingTo")}</p>
                <p>{address.name}</p>
                <p>{address.address1}{address.address2 ? `, ${address.address2}` : ""}</p>
                <p>{address.postal_code} {address.city}</p>
                <button onClick={() => setStep("address")} className="mt-1 text-xs text-primary underline">
                  {t("merch.editAddress")}
                </button>
              </div>
            )}

            {/* Promo */}
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Tag className="h-3.5 w-3.5" />
                {t("merch.promoCode")}
              </p>
              {appliedPromo ? (
                <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/10 p-2">
                  <span className="text-sm font-medium text-primary">{appliedPromo.code} {appliedPromo.freeSticker ? `(${t("merch.checkoutDialog.freeStickerTag")})` : `(-${appliedPromo.discount}%)`}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => { setAppliedPromo(null); setPromoCode(""); }}>
                    {t("merch.checkoutDialog.remove")}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                    placeholder={t("merch.checkoutDialog.enterCode")}
                    className="h-9 text-sm"
                  />
                  <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={handleApplyPromo} disabled={!promoCode.trim()}>
                    {t("merch.checkoutDialog.apply")}
                  </Button>
                </div>
              )}
              {promoError && <p className="text-xs text-destructive">{promoError}</p>}
            </div>

            {/* Order summary */}
            <div className="space-y-2 rounded-lg border border-border/50 p-3 text-sm">
              <p className="text-sm font-medium text-foreground">{t("merch.orderSummary")}</p>
              <div className="flex justify-between text-muted-foreground">
                <span>{t("merch.cart.subtotal")}</span>
                <span className={appliedPromo ? "line-through" : ""}>{displayPrice(subtotalDKK, currency)}</span>
              </div>
              {appliedPromo && (
                <div className="flex justify-between text-xs text-primary">
                  <span>{appliedPromo.freeSticker ? t("merch.checkoutDialog.freeStickerLine") : t("merch.checkoutDialog.discount", { percent: appliedPromo.discount })}</span>
                  <span>-{displayPrice(discountAmount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>{deliveryType === "express" ? t("merch.checkoutDialog.expressShipping") : t("merch.checkoutDialog.standardShipping")}</span>
                <span className="flex items-center gap-1.5">
                  {activeDelivery.originalDisplay && (
                    <span className="text-xs line-through">{activeDelivery.originalDisplay}</span>
                  )}
                  <span className={activeDelivery.discountLabel ? "font-medium text-primary" : ""}>{activeDelivery.display}</span>
                </span>
              </div>
              {processingFeeDKK > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("merch.checkoutDialog.processingFee")}</span>
                  <span>{displayPrice(processingFeeDKK, currency)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 font-semibold text-foreground">
                <span>{t("merch.total")}</span>
                <span className="text-primary">{displayPrice(totalDKK, currency)}</span>
              </div>
            </div>

            <TermsOfSaleCheckbox id="terms-merch-cart-checkout" checked={termsAccepted} onCheckedChange={setTermsAccepted} />

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep("address")}>
                {t("common.back")}
              </Button>
              <Button className="relative flex-1 gap-2 overflow-hidden" onClick={handleCheckout} disabled={!termsAccepted || loading}>
                <div className="shimmer-sweep absolute inset-0" />
                {loading && <Loader2 className="relative z-10 h-4 w-4 animate-spin" />}
                <span className="relative z-10">{t("merch.payNow")}</span>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
